import { useEffect, useState } from "react";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { deleteData, fetchData, postData } from "../../../services";
import UploadFile from "../../Utils/UploadFile";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setRowsPerPage,
  setTotalPages,
} from "./store/motorCategoryStore";
import { useSnackbar } from "../../../context/popup";
import { useLoading } from "../../../components/Loading";
import MotorCategoryFilterDialog from './components/MotorCategoryFilterDialog.js';

const MotorCategoryList = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const { setCircleLoading } = useLoading();

  const currentPage = useSelector(
    (state) => state?.motorCategoryStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.motorCategoryStore?.rowsPerPage
  );
  const totalPages = useSelector(
    (state) => state?.motorCategoryStore?.totalPages
  );

  const [categoryData, setCategoryData] = useState([]);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [clearFileOnSuccess, setClearFileOnSuccess] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
      isActive: "",
      parentCategoryId: ""
  });

  //fetch list motor category
  const fetchMotorCategoryData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/motor/key-value-category?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}&parentCategoryId=${filterData.parentCategoryId}`;
                                    
      //filter
      if(filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }
      const response = await fetchData(url);
      if (response?.success) {
        setCategoryData(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    } finally {
      setCircleLoading(false);
    }
  };

  useEffect(() => {
    fetchMotorCategoryData();
  }, [isRefresh, currentPage, rowsPerPage, search]);

  //bulk file upload api function
  const handleFileUploadSuccess = async (file) => {
    setFileUploading(true);
    setUploadSuccess(false);

    try {
      const formData = new FormData();

      formData.append("file", file);

      const result = await postData(
        `/reference/master/bulkUpload/motor-key-value-category`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (result?.success) {
        setUploadSuccess(true);
        showSnackbar(result?.message, "success");
        setOpenUploadFileDialog(false);
        setClearFileOnSuccess(true);
        fetchMotorCategoryData();
      } else {
        setUploadSuccess(false);
        showSnackbar("Faiiled to upload file", "error");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      console.error("Upload error:", error || errorMessage);
      showSnackbar(errorMessage, "error");
    } finally {
      setFileUploading(false);
    }
  };

  //delete api function
  const handleDelete = async (row) => {
    try {
      const response = await deleteData(
        `/reference/motor/key-value-category-delete`,
        {
          ids: [row?.id],
        }
      );
      setIsRefresh(!isRefresh);

      if (response?.success) {
        showSnackbar(response?.message, "success");
      } else {
        showSnackbar(response?.message, "error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar(error?.response?.data?.message, "error");
    }
  };

  //table columns
  const columns = [
    {
      key: "sno",
      label: "S.No",
      renderCell: (row) => row?.sno,
    },
    {
      key: "code",
      label: "Code",
      renderCell: (row) => row?.code,
    },
    {
      key: "categoryName",
      label: "Category Name",
      renderCell: (row) => row?.categoryName,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => row?.createdAt,
    },
    {
      key: "active",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row?.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      ),
    },
  ];

  //table rows
  const rows = categoryData.map((category, index) => ({
    ...category,
    sno: index + 1,
    createdAt: new Date(category.createdAt).toLocaleDateString("en-GB"),
  }));

  const actions = {
    view: true,
    delete: true,
    onDelete: (row) => handleDelete(row),
    onView: (row) => handleView(row),
  };

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleView = (row) => {
    navigate(`/motor-master/motor-category/view-category/${row?.id}`);
  };

  const handleAddNewCategory = () => {
    navigate("/motor-master/motor-category/add-category");
  };

  const handleFileUpload = () => {
    setOpenUploadFileDialog(true);
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <DashboardLayout>
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Motor Category management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of Categories that you can get details of the category.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
              variant="gradient"
              color="dark"
              size="small"
              startIcon={<FilterAlt/>}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              >
              Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => handleFileUpload()}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewCategory()}
              >
                Add Category
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          className="custom-table"
          noDataLabel={"No motor category data found"}
          handleSearch={handleSearch}
        />
      </Card>
      <MotorCategoryFilterDialog
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      filterData={filterData}
      setFilterData={setFilterData}
      onApply={() => {
          setAnchorEl(null);   // close popup
          fetchMotorCategoryData();  // call API after filter applied
      }}
      />
      <UploadFile
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        onUploadSuccess={handleFileUploadSuccess}
        fileTypes={["csv", "xlsx"]}
        fileUploading={fileUploading}
        uploadSuccess={uploadSuccess}
        setUploadSuccess={setUploadSuccess}
        setFileUploading={setFileUploading}
        clearFileOnSuccess={clearFileOnSuccess}
      />
    </DashboardLayout>
  );
};

export default MotorCategoryList;
